export type VisitorName = { firstName: string; lastName: string; fullName: string };

// A name answer can also contain a greeting or a question. Never treat the
// entire answer as a name just because it arrived after "What's your name?"
const NAME_TOKEN = new RegExp("^[\\p{L}\\p{M}][\\p{L}\\p{M}'’\\-]*$", "u");
const NON_NAME_WORDS = new Set([
  "hi", "hello", "hey", "there", "name", "is", "am", "i", "im", "my",
  "looking", "into", "getting", "get", "training", "session", "sessions",
  "interested", "want", "need", "like", "have", "help", "information", "info",
  "pricing", "price", "options", "available", "for", "about", "with", "to",
  "what", "how", "where", "when", "can", "could", "would", "please", "thanks",
  "thank", "you", "your", "our", "the", "a", "an", "someone", "daughter", "son",
  "email", "phone", "number", "and", "that", "this", "it", "just", "on", "in",
  "at", "from", "because",
]);

function parseNamePhrase(phrase: string): VisitorName | null {
  const words = phrase.trim().split(/\s+/).filter(Boolean);
  if (!words.length || words.length > 7 || phrase.length > 100) return null;
  if (words.some((word) => !NAME_TOKEN.test(word) || NON_NAME_WORDS.has(word.toLocaleLowerCase()))) {
    return null;
  }
  return {
    firstName: words[0],
    lastName: words.slice(1).join(" "),
    fullName: words.join(" "),
  };
}

export function parseVisitorNameAnswer(answer: string, introductionsOnly = false): VisitorName | null {
  const text = answer.trim();
  if (!text) return null;
  const introductions = Array.from(text.matchAll(/\b(?:my name is|my name(?:'|’)s|i(?:'|’)m|i am|this is)\s+/gi));
  // Prefer the last clear self-introduction: "Hello. My name is Germaine..."
  // The earlier "My name is Hello" is not the visitor's final answer.
  for (const intro of introductions.reverse()) {
    const after = text
      .slice(intro.index! + intro[0].length)
      .split(/[,.!?;:\n]|\s+and\s+(?=(?:my\s+)?(?:email|phone|number)\b)/i, 1)[0];
    const parsed = parseNamePhrase(after);
    if (parsed) return parsed;
  }
  if (introductionsOnly || introductions.length) return null;
  const withoutGreeting = text.replace(/^(?:hi|hello|hey)\s*[,!.]\s*/i, "");
  return parseNamePhrase(withoutGreeting.split(/[,.!?;:\n]/, 1)[0]);
}

export function plausibleVisitorNameFields(firstName?: string, lastName?: string): boolean {
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim().replace(/\s+/g, " ");
  const parsed = parseVisitorNameAnswer(fullName);
  return !!parsed && parsed.fullName === fullName;
}

// Free-form chat has no dedicated name field. Use only the visitor's own
// introduction or their answer to a direct name question, not the model's
// interpretation of a greeting, an email address, or someone else's name.
export function visitorNameFromConversation(messages: Array<{ role: string; content: string }>): VisitorName | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role !== "user") continue;
    const introduced = parseVisitorNameAnswer(messages[i].content, true);
    if (introduced) return introduced;
    const previous = messages[i - 1];
    if (previous?.role === "assistant" && /\byour last name\b/i.test(previous.content)) {
      const earlierName = visitorNameFromConversation(messages.slice(0, i - 1));
      const surname = parseVisitorNameAnswer(
        messages[i].content.replace(/^(?:my last name is|last name is|it's)\s+/i, ""),
      );
      if (earlierName && surname) {
        const lastName = surname.firstName.toLocaleLowerCase() === earlierName.firstName.toLocaleLowerCase() && surname.lastName
          ? surname.lastName
          : surname.fullName;
        return { firstName: earlierName.firstName, lastName, fullName: `${earlierName.firstName} ${lastName}` };
      }
      continue;
    }
    if (
      previous?.role === "assistant" &&
      /\b(?:what(?:'|’)s your name|what is your name|your (?:(?:first|full) )?name|who am i speaking with|who should (?:we|i) ask for)\b/i.test(previous.content)
    ) {
      // "It's Casey" is a reasonable answer to a direct name question, but
      // "It's Friday" (or a reference to someone else) in free chat is not
      // evidence of the visitor's name. Only treat this contraction as a name
      // prefix in the context where the visitor was explicitly asked.
      const answer = messages[i].content.replace(/^\s*it(?:'|’)s\s+/i, "");
      const answered = parseVisitorNameAnswer(answer);
      if (answered) return answered;
    }
  }
  return null;
}